/**
 * localStorage-based backend replacing the ICP canister.
 * Provides the same data interface so all hooks/pages work unchanged.
 */

// ---- Types (matching backend.d.ts) ----

export enum DefectType {
    scratch = 'scratch',
    crack = 'crack',
    warp = 'warp',
    discoloration = 'discoloration',
}

export enum Severity {
    good = 'good',
    minorDefect = 'minorDefect',
    majorDefect = 'majorDefect',
}

export interface InspectionRecord {
    id: number;
    defectType: DefectType;
    modelConfidence: number;
    imageReference: string; // base64 data URL or empty
    severityScore: number;
    componentId: string;
    timestamp: number; // ms since epoch
    finalClassification: Severity;
}

export interface UserProfile {
    name: string;
    role: string;
}

// ---- Storage keys ----
const RECORDS_KEY = 'visioninspect_records';
const PROFILE_KEY = 'visioninspect_profile';
const SEEDED_KEY = 'visioninspect_seeded';

// ---- Helpers ----

function loadRecords(): InspectionRecord[] {
    try {
        const raw = localStorage.getItem(RECORDS_KEY);
        return raw ? JSON.parse(raw) : [];
    } catch {
        return [];
    }
}

function saveRecords(records: InspectionRecord[]) {
    localStorage.setItem(RECORDS_KEY, JSON.stringify(records));
}

let nextId = Date.now();
function genId(): number {
    return nextId++;
}

// ---- Seed data ----

function seedIfNeeded() {
    if (localStorage.getItem(SEEDED_KEY)) return;

    const now = Date.now();
    const DAY = 86_400_000;

    const seed: InspectionRecord[] = [
        { id: genId(), defectType: DefectType.scratch, modelConfidence: 94, imageReference: '', severityScore: 22, componentId: 'BRG-4821-A', timestamp: now - DAY * 6, finalClassification: Severity.minorDefect },
        { id: genId(), defectType: DefectType.crack, modelConfidence: 97, imageReference: '', severityScore: 78, componentId: 'GER-1193-B', timestamp: now - DAY * 5, finalClassification: Severity.majorDefect },
        { id: genId(), defectType: DefectType.warp, modelConfidence: 91, imageReference: '', severityScore: 35, componentId: 'BRN-7742-C', timestamp: now - DAY * 5, finalClassification: Severity.minorDefect },
        { id: genId(), defectType: DefectType.scratch, modelConfidence: 93, imageReference: '', severityScore: 5, componentId: 'GRN-3321-A', timestamp: now - DAY * 4, finalClassification: Severity.good },
        { id: genId(), defectType: DefectType.discoloration, modelConfidence: 89, imageReference: '', severityScore: 12, componentId: 'BRG-9054-D', timestamp: now - DAY * 4, finalClassification: Severity.good },
        { id: genId(), defectType: DefectType.crack, modelConfidence: 96, imageReference: '', severityScore: 65, componentId: 'GER-6678-A', timestamp: now - DAY * 3, finalClassification: Severity.majorDefect },
        { id: genId(), defectType: DefectType.scratch, modelConfidence: 92, imageReference: '', severityScore: 8, componentId: 'BRG-2210-B', timestamp: now - DAY * 3, finalClassification: Severity.good },
        { id: genId(), defectType: DefectType.warp, modelConfidence: 90, imageReference: '', severityScore: 28, componentId: 'GRN-5519-C', timestamp: now - DAY * 2, finalClassification: Severity.minorDefect },
        { id: genId(), defectType: DefectType.discoloration, modelConfidence: 88, imageReference: '', severityScore: 6, componentId: 'BRN-8843-A', timestamp: now - DAY * 2, finalClassification: Severity.good },
        { id: genId(), defectType: DefectType.scratch, modelConfidence: 95, imageReference: '', severityScore: 42, componentId: 'GER-1105-D', timestamp: now - DAY * 1, finalClassification: Severity.minorDefect },
        { id: genId(), defectType: DefectType.crack, modelConfidence: 96, imageReference: '', severityScore: 71, componentId: 'BRG-3390-B', timestamp: now - DAY * 1, finalClassification: Severity.majorDefect },
        { id: genId(), defectType: DefectType.scratch, modelConfidence: 93, imageReference: '', severityScore: 4, componentId: 'GRN-7782-A', timestamp: now, finalClassification: Severity.good },
    ];

    saveRecords(seed);
    localStorage.setItem(SEEDED_KEY, '1');
}

// Run seed on module load
seedIfNeeded();

// ---- Public API ----

export const localBackend = {
    // Inspection records
    getAllInspectionRecords(): InspectionRecord[] {
        return loadRecords();
    },

    getRecentInspections(count: number): InspectionRecord[] {
        const records = loadRecords();
        records.sort((a, b) => b.timestamp - a.timestamp);
        return records.slice(0, count);
    },

    getInspectionRecord(id: number): InspectionRecord | null {
        return loadRecords().find((r) => r.id === id) ?? null;
    },

    addInspectionRecord(record: Omit<InspectionRecord, 'id'> & { id?: number }): void {
        const records = loadRecords();
        const newRecord: InspectionRecord = {
            ...record,
            id: record.id || genId(),
        };
        records.push(newRecord);
        saveRecords(records);
    },

    deleteInspectionRecord(id: number): void {
        const records = loadRecords().filter((r) => r.id !== id);
        saveRecords(records);
    },

    // KPIs
    getKPIs(): { totalInspections: number; rejectionRate: number; majorDefectCount: number; modelAccuracy: number } {
        const records = loadRecords();
        const total = records.length;
        const major = records.filter((r) => r.finalClassification === Severity.majorDefect).length;
        const nonGood = records.filter((r) => r.finalClassification !== Severity.good).length;
        const avgConfidence = total > 0 ? Math.round(records.reduce((sum, r) => sum + r.modelConfidence, 0) / total) : 90;
        return {
            totalInspections: total,
            rejectionRate: total > 0 ? Math.round((nonGood / total) * 100) : 0,
            majorDefectCount: major,
            modelAccuracy: avgConfidence,
        };
    },

    // Trends: group by day
    getInspectionTrends(): Array<[number, number]> {
        const records = loadRecords();
        const dayMap = new Map<string, { ts: number; count: number }>();
        for (const r of records) {
            const dayKey = new Date(r.timestamp).toISOString().slice(0, 10);
            const existing = dayMap.get(dayKey);
            if (existing) {
                existing.count++;
            } else {
                dayMap.set(dayKey, { ts: r.timestamp, count: 1 });
            }
        }
        const entries = Array.from(dayMap.values());
        entries.sort((a, b) => a.ts - b.ts);
        return entries.map((e) => [e.ts, e.count]);
    },

    // Defect distribution
    getDefectDistribution(): Array<[DefectType, number]> {
        const records = loadRecords();
        const counts = new Map<DefectType, number>();
        for (const dt of Object.values(DefectType)) {
            counts.set(dt, 0);
        }
        for (const r of records) {
            counts.set(r.defectType, (counts.get(r.defectType) ?? 0) + 1);
        }
        return Array.from(counts.entries());
    },

    // Severity distribution
    getSeverityDistribution(): Array<[Severity, number]> {
        const records = loadRecords();
        const counts = new Map<Severity, number>();
        for (const sev of Object.values(Severity)) {
            counts.set(sev, 0);
        }
        for (const r of records) {
            counts.set(r.finalClassification, (counts.get(r.finalClassification) ?? 0) + 1);
        }
        return Array.from(counts.entries());
    },

    // Classifications stats
    getClassificationsStats(): {
        defectStats: Array<[DefectType, number]>;
        rejectionStats: Array<[Severity, number]>;
    } {
        return {
            defectStats: localBackend.getDefectDistribution(),
            rejectionStats: localBackend.getSeverityDistribution(),
        };
    },

    getRecordsByDefectType(defectType: DefectType): InspectionRecord[] {
        return loadRecords().filter((r) => r.defectType === defectType);
    },

    getRecordsBySeverity(severity: Severity): InspectionRecord[] {
        return loadRecords().filter((r) => r.finalClassification === severity);
    },

    // Today stats (for Operator dashboard)
    getTodayStats(): { todayCount: number; todayPassRate: number; lastInspection: InspectionRecord | null } {
        const records = loadRecords();
        const todayStart = new Date();
        todayStart.setHours(0, 0, 0, 0);
        const todayTs = todayStart.getTime();
        const todayRecords = records.filter((r) => r.timestamp >= todayTs);
        const todayGood = todayRecords.filter((r) => r.finalClassification === Severity.good).length;
        const sorted = [...records].sort((a, b) => b.timestamp - a.timestamp);
        return {
            todayCount: todayRecords.length,
            todayPassRate: todayRecords.length > 0 ? Math.round((todayGood / todayRecords.length) * 100) : 100,
            lastInspection: sorted[0] || null,
        };
    },

    // User profile
    getCallerUserProfile(): UserProfile | null {
        try {
            const raw = localStorage.getItem(PROFILE_KEY);
            return raw ? JSON.parse(raw) : null;
        } catch {
            return null;
        }
    },

    saveCallerUserProfile(profile: UserProfile): void {
        localStorage.setItem(PROFILE_KEY, JSON.stringify(profile));
    },
};
